/**
 * Toasts and confirm dialogs for code outside React (qaEngine.js drives its page by hand), and a
 * shorter path for anyone else. ToastProvider (context/ToastContext.js) listens for these events
 * and shows the suite's own toast and ConfirmModal, in place of the browser's alert() and
 * confirm(), which freeze the page, cannot be styled and are suppressed by embedded browsers.
 *
 *   notify('Saved', 'success');                // 'error' (default) | 'success' | 'info' | 'warning'
 *   if (!(await confirmAsync({ title: 'Delete this report?', message: 'This cannot be undone.' }))) return;
 */
export const TOAST_EVENT = 'suite:toast';
export const CONFIRM_EVENT = 'suite:confirm';

export function notify(message, type = 'error') {
  window.dispatchEvent(new CustomEvent(TOAST_EVENT, { detail: { message: String(message ?? ''), type } }));
}

export function confirmAsync({ title = 'Are you sure?', message = '', confirmLabel = 'Confirm', closeLabel = 'Cancel' } = {}) {
  return new Promise((resolve) => {
    const detail = { title, message, confirmLabel, closeLabel, resolve, handled: false };
    window.dispatchEvent(new CustomEvent(CONFIRM_EVENT, { detail }));
    // No provider mounted (a test, say): fall back to the browser's own dialog.
    if (!detail.handled) resolve(window.confirm([title, message].filter(Boolean).join('\n\n')));
  });
}

export const PROMPT_EVENT = 'suite:prompt';

/**
 * `window.prompt`, in-app: a dialog with one text box. Resolves with what was typed (not trimmed, so the caller decides
 * what an empty answer means) or `null` on Cancel, Escape or a click outside.
 *
 *   const name = await promptAsync({ title: 'New team name', label: 'Team name', confirmLabel: 'Add team' });
 *   if (name === null) return;
 */
export function promptAsync({ title = 'Enter a value', message = '', label = '', placeholder = '', defaultValue = '', confirmLabel = 'OK', closeLabel = 'Cancel' } = {}) {
  return new Promise((resolve) => {
    const detail = { title, message, label, placeholder, defaultValue: String(defaultValue ?? ''), confirmLabel, closeLabel, resolve, handled: false };
    window.dispatchEvent(new CustomEvent(PROMPT_EVENT, { detail }));
    // No provider mounted (a test, say): fall back to the browser's own prompt.
    if (!detail.handled) resolve(window.prompt([title, message].filter(Boolean).join('\n\n'), detail.defaultValue));
  });
}
